import {
    Button,
    DataTable,
    DialogHandler,
    EmptyContent,
    EStorageType,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    getStorage,
    Header,
    Icon,
    isDomainValidService,
    LoadingContainer,
    Search,
    SenderModel,
    SendersFactory,
    setStorage,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TOrder
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { getDomain } from "../../../utils/emailUtils"
import { ERoutes } from "../../../utils/url"
import { SenderRow } from "../components/senderRow"
import { SEARCH_AND_SORT_STORAGE_KEY } from "../contants"
import { EDomainAuthStatus, TSendersListing } from "../types"

export default function SendersListing({ setSingleSender }: TSendersListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const persistedState = getPersistedState()

    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState<string>(persistedState.search || "")
    const [openedSearch, setOpenedSearch] = useState(false)
    const [order, setOrder] = useState<TOrder>(persistedState.order || "asc")
    const [orderBy, setOrderBy] = useState<string>(persistedState.orderBy || "name")
    const [senders, setSenders] = useState<TGenericListReturn<SenderModel> | undefined>()
    const [domains, setDomains] = useState<Record<string, any>>({})

    const sendersTimerRef = useRef<NodeJS.Timeout | null>(null)
    const hasUnverifiedSendersRef = useRef<boolean>(false)
    const hasFetchedDomainsRef = useRef<boolean>(false)

    useEffect(() => {
        fetchSenders()
    }, [order, orderBy])

    useEffect(() => {
        if (senders?.data && !hasFetchedDomainsRef.current) {
            fetchDomains()
            hasFetchedDomainsRef.current = true
        }
    }, [senders])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        return () => {
            stopAutoRefresh()
        }
    }, [])

    useEffect(() => {
        setStorage({
            key: SEARCH_AND_SORT_STORAGE_KEY,
            data: { search, order, orderBy },
            type: EStorageType.session
        })
    }, [search, order, orderBy])

    function getPersistedState() {
        const cached = getStorage({
            key: SEARCH_AND_SORT_STORAGE_KEY,
            type: EStorageType.session
        })
        return cached || {}
    }

    function fetchSenders() {
        setLoading(true)

        SendersFactory.list({
            page: 1,
            perPage: 200,
            withCount: true,
            sort: order === 'asc' ? orderBy : `-${orderBy}`
        })
            .then((data) => {
                setSenders(data)
                setLoading(false)
                const hasUnverified = data.data.some((sender: SenderModel) => !sender.confirmed)

                if (hasUnverified && !hasUnverifiedSendersRef.current) {
                    hasUnverifiedSendersRef.current = true
                    startAutoRefresh()
                }

                if (!hasUnverified && hasUnverifiedSendersRef.current) {
                    hasUnverifiedSendersRef.current = false
                    stopAutoRefresh()
                }
            })
            .catch(() => {
                setLoading(false)
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("app-senders-error-fetch")
                })
            })
    }

    function fetchDomains() {
        if (!senders?.data) return

        const uniqueDomains = new Set<string>()
        senders.data.forEach((sender: SenderModel) => {
            uniqueDomains.add(getDomain(sender))
        })

        uniqueDomains.forEach(domain => {
            verifyDomain(domain)
        })
    }

    function verifyDomain(domain: string) {
        isDomainValidService({
            domain,
            useImpersonationTree: false
        })
            .then((data) => {
                let authenticatedStatus: EDomainAuthStatus = EDomainAuthStatus.Unauthenticated

                if (data?.blocked) {
                    authenticatedStatus = EDomainAuthStatus.Blocked
                } else if (data?.verified) {
                    authenticatedStatus = EDomainAuthStatus.Authenticated
                }

                setDomains(prev => ({
                    ...prev,
                    [domain]: {
                        ...prev[domain],
                        ...data,
                        verified: data?.verified || false,
                        authenticated: authenticatedStatus
                    }
                }))
            })
            .catch(() => {
                setDomains(prev => ({
                    ...prev,
                    [domain]: {
                        ...prev[domain],
                        verified: false,
                        authenticated: "unauthenticated"
                    }
                }))
            })
    }

    function startAutoRefresh() {
        if (sendersTimerRef.current) {
            clearInterval(sendersTimerRef.current)
        }

        sendersTimerRef.current = setInterval(() => {
            fetchSenders()
        }, 15000)
    }

    function stopAutoRefresh() {
        if (sendersTimerRef.current) {
            clearInterval(sendersTimerRef.current)
            sendersTimerRef.current = null
        }
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)
    }

    function handleSearchOpen() {
        setOpenedSearch(!openedSearch)
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".sendersSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handleCreateSender() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.sendersCreate })
    }

    function handleViewSender(sender: SenderModel) {
        if (setSingleSender) {
            setSingleSender(sender)
        }
        window.location.href = "#" + fetchRoute({ route: ERoutes.senderEdit, params: { id: sender.id } })
    }

    function handleDeleteSender(sender: SenderModel) {

        showDeleteDialog({
            name: sender.name || sender.email,
            resourceKey: "general-sender_one"
        })
            .then(() => {
                if (!sender.id) throw Error
                return sender.delete()
            })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("general-resource-deleted", { resource: t("general-sender_one") })
                })
                fetchSenders()
            })
            .catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action")
                    })
                }
            })
    }

    async function handleResendConfirmation(sender: SenderModel) {
        setLoading(true)
        return sender.resendVerificationEmail()
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("users-user-resend-verification-done")
                })
            })
            .catch(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("users-user-resend-verification-error")
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    function handleAuthenticateDomain(domain: string) {
        window.location.href = "#" + fetchRoute({
            route: ERoutes.domainAuthenticate,
            params: { domain }
        })
    }

    const filteredSenders = useMemo(() => {
        if (!senders) return []
        if (!search) return senders.data

        const searchLower = search.toLowerCase()
        return senders.data.filter((sender: SenderModel) => {
            return (
                sender.name?.toLowerCase().includes(searchLower) ||
                sender.email.toLowerCase().includes(searchLower)
            )
        })
    }, [senders, search])

    const colsData: TDataTableColumnsData[] = useMemo(() => [
        {
            id: "name",
            label: t("general-name"),
            sortable: true
        },
        {
            id: "email",
            label: t("general-email-address"),
            sortable: true
        },
        {
            id: "domain",
            label: t("general-domain_one")
        },
        {
            id: "domain_authenticated",
            label: t("general-domain-authenticated_one")
        },
        {
            id: "confirmed_on",
            label: t("general-confirmed-on")
        },
        {
            id: "actions",
            label: ""
        }
    ], [])

    const rows: any[] = useMemo(() => {
        return filteredSenders.map((sender: SenderModel, index: number) => {
            const atIndex = sender.email.indexOf("@")
            const domain = sender.email.substring(atIndex + 1)

            const domainInfo = domains[domain]

            return SenderRow({
                sender,
                index,
                loading,
                domainInfo,
                handleViewSender,
                handleDeleteSender,
                handleResendConfirmation,
                handleAuthenticateDomain
            })
        })
    }, [filteredSenders, domains, loading])

    return <Stack gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-senders_domain")}
                headingTooltip={t("general-manage-senders-domains")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
            />}
            right={[
                <Button
                    key="create-sender"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreateSender}
                    disabled={loading}
                >
                    <Icon name="Add18" />
                </Button>,
                <Button
                    key="search"
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                >
                    <Icon name="Search18" />
                </Button>,
                <Button
                    key="tracking-domains-overlay"
                    size="medium"
                    color="body1"
                    variant="outlined"
                >{t("general-tracking-domain_other")}</Button>
            ]}
        />

        {openedSearch &&
            <Search
                placeholder={t("senders-listing-search-placeholder")}
                initValue={search}
                className={`sendersSearch ${loading ? "loading" : ""}`}
                onChange={handleSearchChange}
                onClose={handleSearchOpen}
                useClickAway
            />
        }

        <LoadingContainer loading={!senders}>
            {senders && filteredSenders.length === 0 && !search &&
                <EmptyContent
                    title={t("app-senders-listing-empty-page-no-result")}
                    desc={t("app-general-no-results-desc")}
                    button={
                        <Button
                            variant="contained"
                            onClick={handleCreateSender}
                            size="medium"
                            color="secondary"
                        >
                            {t("app-account-sender-create-button")}
                        </Button>
                    }
                />
            }

            {senders && filteredSenders.length === 0 && search &&
                <EmptyContent
                    title={t("app-senders-listing-empty-page-no-result")}
                    desc={t("app-general-no-results-desc")}
                />
            }

            {senders && filteredSenders.length > 0 &&
                <DataTable
                    initState={{
                        order,
                        orderBy
                    }}
                    totalCount={senders.pagination.count}
                    className={`sendersDataTable ${loading ? "loading" : ""}`}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                    showPagination={false}
                />
            }
        </LoadingContainer>
    </Stack>
}